import { isPromoReady } from 'src/replicant/promo/promo.getters';
import StateObserver, { user } from 'src/StateObserver';
import type { EntryProperties } from './analytics/properties';
import { GCInstant } from './gcinstant';
import { captureGenericError } from './sentry';
import { requestReview } from './native/native.util';
import { track } from './analytics/utils';
import { isFirstEntryOfToday } from 'src/replicant/entry/entry.getters';
import { isPlatformLiff } from 'src/game/crossplay/crossplay.utils';

const lineSettingsAppLink = 'https://line.me/R/nv/connectedApps';

/**
 * Is this new user and it a first time this user launched a game
 */
export function isFirstEntry() {
  return user().loginCount === 1;
}

/**
 * Check if it's the first entry of the day, based on the current time and platform.
 * @returns
 */
export function isFirstEntryOfDay(): boolean {
  const now = StateObserver.now();
  // Check if it's one of native apps
  if (GCInstant.osType === 'IOS_APP' || GCInstant.osType === 'ANDROID_APP') {
    return isFirstEntryOfToday(user(), GCInstant.osType, now);
  }
  // Check if it's LIFF
  if (isPlatformLiff()) {
    isFirstEntryOfToday(user(), 'LIFF', now);
  }
  // Default to web
  return isFirstEntryOfToday(user(), 'WEB', now);
}

export function openLineSettings() {
  if (GCInstant.osType === 'IOS_APP') {
    window.location.assign(lineSettingsAppLink);
  } else {
    window.open(lineSettingsAppLink);
  }
}

export function getEntryData(): EntryProperties {
  return GCInstant.entryData;
}

/**
 * Prompt user to rate app.
 * @returns
 */
export async function requestRateApp() {
  // Check if app review requesting is available
  if (!GCInstant.legacyNative?.store.isAvailable()) return;

  // Use different promo ids for different platforms,
  // to be able to handle their ask-review policies separately.
  const isAndroid = GCInstant.osType === 'ANDROID_APP';
  const promoId = isAndroid ? 'rateAppAndroid' : 'rateAppIOS';

  // Check if rate app is on cooldown.
  const { user } = StateObserver.getState();
  if (!isPromoReady(user, promoId, StateObserver.now())) return;

  // Show rate app dialog.
  try {
    requestReview();
    await StateObserver.invoke.promoWasShown(promoId);
    track('RatingRequestPopup', {});
  } catch (e) {
    captureGenericError('Could not request store review.', e);
  }
}
